import { useEffect, useRef, useState } from 'react'
import {
  useTransformerLoading,
  useFeederLoadingSummary,
  usePhaseBalance,
  useVoltageDrop,
} from '../../hooks/useEngineering.ts'
import { friendlyError } from '../../utils/friendlyError.ts'
import { CapacitySummary } from './CapacitySummary.tsx'
import { AsOfStamp } from './AsOfStamp.tsx'
import { TransformerLoadingTable } from './TransformerLoadingTable.tsx'
import { PhaseBalanceChart } from './PhaseBalanceChart.tsx'
import { VoltageDropSummary } from './VoltageDropSummary.tsx'
import { WhatIfPanel } from './WhatIfPanel.tsx'
import { OpenDSSRunPanel } from './OpenDSSRunPanel.tsx'
import { DataQualityTab } from './DataQualityTab.tsx'
import { DeviceRatingCoverageCard } from './DeviceRatingCoverageCard.tsx'
import { WarningSummary } from './WarningSummary.tsx'
import { FaultAnalysis } from './FaultAnalysis.tsx'
import { FaultStudyCompare } from './FaultStudyCompare.tsx'
import { DeviceDuty } from './DeviceDuty.tsx'
import { DutyRollupChips, DEVICE_DUTY_CARD_ID } from './DutyRollupChips.tsx'
import { ProtectionTree } from './ProtectionTree.tsx'
import { ArcFlashSummary } from './ArcFlashSummary.tsx'
import { AmiMeasuredTab } from './AmiMeasuredTab.tsx'
import { AboutAnalysis } from './AboutAnalysis.tsx'
import { useSelectedFeeder } from '../../hooks/useSelectedFeeder.ts'
import { useAmiStatus } from '../../hooks/useAmiAnalytics.ts'
import {
  useDeviceRatingCoverage,
  useFaultStudyCombined,
  useProtectionTree,
} from '../../hooks/useFaultAnalysis.ts'
import { calibratedAvgDemandKw } from '../../utils/loading.ts'
import { sortByLabel } from '../../utils/feederSort.ts'
import type { LoadSource } from '../../types/engineering.ts'
import { useLocation } from 'react-router-dom'
import {
  deepLinkFeederId,
  initialEngineeringTab,
  initialStatusFilter,
  type EngineeringTab,
} from './engineeringTab.ts'

type Tab = EngineeringTab

export function EngineeringDashboard() {
  // Cross-view feeder selection (shared with the map). If a feeder was already
  // selected elsewhere (e.g. clicked on the map), open straight to its detail.
  const { selectedFeederId, setSelectedFeederId } = useSelectedFeeder()
  // Read-once deep links from the Home cards (UX spec §3.8): ?tab= picks the
  // tab, ?feeder= picks the feeder (and therefore the detail tab), ?status=
  // seeds the loading table's filter. Never re-read after mount.
  const location = useLocation()
  const deepLinkFeeder = deepLinkFeederId(location.search)
  const deepLinkStatus = initialStatusFilter(location.search)
  const [tab, setTab] = useState<Tab>(() =>
    initialEngineeringTab(location.search, deepLinkFeeder ?? selectedFeederId),
  )

  // Push a ?feeder= id into the SHARED selection once, so the feeder detail
  // tab (gated on selectedFeederId) has something to render. The ref keeps it
  // read-once while leaving the dep array honest.
  const deepLinkFeederApplied = useRef(false)
  useEffect(() => {
    if (!deepLinkFeederApplied.current && deepLinkFeeder != null) {
      deepLinkFeederApplied.current = true
      setSelectedFeederId(deepLinkFeeder)
    }
  }, [deepLinkFeeder, setSelectedFeederId])

  // Avg-demand slider: sliderKw follows the pointer; avgDemandKw is the
  // debounced value that feeds query keys (300 ms after the last tick), so a
  // drag no longer fires a request burst per tick (spec §3.5).
  const [sliderKw, setSliderKw] = useState(5.0)
  const [avgDemandKw, setAvgDemandKw] = useState(5.0)
  const [sliderTouched, setSliderTouched] = useState(false)
  // Loading basis for the overview AND feeder tab. Default to AMI measured
  // load; falls back to the model estimate per-transformer when AMI data is
  // missing (spec §3.4 one convention everywhere).
  const [loadSource, setLoadSource] = useState<LoadSource>('measured')

  const { data: amiStatus } = useAmiStatus()
  // Seed the slider from the AMI fleet median (calibrated model default,
  // spec §3.4) until the user touches it; otherwise keep the flat 5.0.
  const calibrated = calibratedAvgDemandKw(
    amiStatus?.fleet_median_coincident_kw_per_meter,
  )
  useEffect(() => {
    if (!sliderTouched && calibrated != null) {
      setSliderKw(calibrated)
      setAvgDemandKw(calibrated)
    }
  }, [sliderTouched, calibrated])

  // Debounce: commit slider ticks to the query-key value after 300 ms.
  useEffect(() => {
    const t = setTimeout(() => setAvgDemandKw(sliderKw), 300)
    return () => clearTimeout(t)
  }, [sliderKw])

  const params = { avg_demand_kw: avgDemandKw }

  // Overview queries — driven by the selected loading basis.
  const { data: loading, isLoading: loadingLoading, error: loadingError } = useTransformerLoading({
    ...params,
    load_source: loadSource,
  })
  const { data: feeders, isLoading: feedersLoading, error: feedersError } = useFeederLoadingSummary({
    ...params,
    load_source: loadSource,
  })

  // Feeder detail queries — only fire when a feeder is selected. The basis
  // passes through so the feeder tab tells the same loading story (spec §3.4).
  const { data: feederLoading, isLoading: feederLoadingLoading, error: feederLoadingError } = useTransformerLoading(
    selectedFeederId
      ? { feeder_id: selectedFeederId, ...params, load_source: loadSource }
      : undefined,
  )
  const { data: phaseBalance, isLoading: phaseLoading, error: phaseError } = usePhaseBalance(
    selectedFeederId,
    avgDemandKw,
  )
  const { data: voltageDrop, isLoading: vdropLoading, error: vdropError } = useVoltageDrop(
    selectedFeederId,
    params,
  )

  // Fault-tab aggregated warnings (A2, spec §3.1). These hooks use the same
  // queryKeys as the fault-tab panels, so react-query dedups them into the
  // panels' own requests — no extra network traffic. Gated to the fault tab
  // so other tabs never fire fault queries early.
  const faultFeederId = tab === 'fault' ? selectedFeederId : null
  const { data: combinedStudy } = useFaultStudyCombined(faultFeederId)
  const { data: protectionResult } = useProtectionTree(faultFeederId)
  const { data: ratingCoverage } = useDeviceRatingCoverage(faultFeederId)
  // Concatenation order mirrors panel order on the tab. The compare panel's
  // snapshot (FaultStudySnapshot) has no warnings field — nothing to add.
  const faultTabWarnings = [
    ...(combinedStudy?.study.warnings ?? []),
    ...(combinedStudy?.duty.warnings ?? []),
    ...(combinedStudy?.arc_flash.warnings ?? []),
    ...(protectionResult?.warnings ?? []),
    ...(ratingCoverage?.warnings ?? []),
  ]

  function handleSelectFeeder(feederId: number) {
    setSelectedFeederId(feederId)
    setTab('feeder')
  }

  function handleBackToOverview() {
    setTab('overview')
    setSelectedFeederId(null)
  }

  const selectedFeederName = feeders?.find(f => f.feeder_id === selectedFeederId)?.feeder_name

  // B6: pickers list feeders alphabetically (the API returns DB order).
  // Sorting keys on the same label string the <option> renders.
  const sortedFeederOptions = feeders
    ? sortByLabel(feeders, f => f.feeder_name || `Feeder #${f.feeder_id}`)
    : undefined

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="max-w-7xl mx-auto p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {(tab === 'feeder' || tab === 'fault' || tab === 'ami' || tab === 'quality') && (
              <button
                onClick={handleBackToOverview}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                &larr; Overview
              </button>
            )}
            <h2 className="text-lg font-semibold text-gray-800">
              {tab === 'overview'
                ? 'Engineering Analysis'
                : tab === 'fault'
                  ? 'Fault Analysis'
                  : tab === 'ami'
                    ? 'AMI Measured'
                    : tab === 'quality'
                      ? 'Data Quality'
                      : `Feeder: ${selectedFeederName || `#${selectedFeederId}`}`}
            </h2>
          </div>

          <div className="flex items-center gap-4">
            {/* Loading basis toggle (overview + feeder tabs — one convention) */}
            {(tab === 'overview' || tab === 'feeder') && (
              <div className="flex flex-col items-end gap-0.5">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500">Loading basis:</label>
                  <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
                    <button
                      onClick={() => setLoadSource('measured')}
                      className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                        loadSource === 'measured'
                          ? 'bg-white text-gray-800 shadow-sm font-medium'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Measured
                    </button>
                    <button
                      onClick={() => setLoadSource('model')}
                      className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                        loadSource === 'model'
                          ? 'bg-white text-gray-800 shadow-sm font-medium'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Estimated
                    </button>
                  </div>
                </div>
                {/* Hybrid banks (adequate coverage but some silent meters) belong
                    to the measured basis: the measured coincident peak carries a
                    flat per-silent-meter estimate on top. */}
                {loadSource === 'measured' && (
                  <span className="text-[10px] text-gray-400">
                    measured basis; +{avgDemandKw} kW per silent meter; model fallback where no AMI
                  </span>
                )}
              </div>
            )}

            {/* Tab buttons */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setTab('overview')}
                className={`text-xs px-3 py-1.5 rounded-md transition-colors ${
                  tab === 'overview'
                    ? 'bg-white text-gray-800 shadow-sm font-medium'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setTab('fault')}
                className={`text-xs px-3 py-1.5 rounded-md transition-colors ${
                  tab === 'fault'
                    ? 'bg-white text-gray-800 shadow-sm font-medium'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Fault Analysis
              </button>
              <button
                onClick={() => setTab('ami')}
                className={`text-xs px-3 py-1.5 rounded-md transition-colors ${
                  tab === 'ami'
                    ? 'bg-white text-gray-800 shadow-sm font-medium'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                AMI Measured
              </button>
              <button
                onClick={() => setTab('quality')}
                className={`text-xs px-3 py-1.5 rounded-md transition-colors ${
                  tab === 'quality'
                    ? 'bg-white text-gray-800 shadow-sm font-medium'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Data Quality
              </button>
            </div>

            {/* Avg demand slider */}
            {tab !== 'fault' && tab !== 'ami' && tab !== 'quality' && (
              <div className="flex items-center gap-3">
                <div className="flex flex-col">
                  <label className="text-xs text-gray-500">Avg Demand:</label>
                  <span className="text-[10px] text-gray-400">
                    model assumption{calibrated != null ? ' — calibrated from AMI' : ''}
                  </span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={15}
                  step={0.5}
                  value={sliderKw}
                  onChange={e => {
                    setSliderTouched(true)
                    setSliderKw(Number(e.target.value))
                  }}
                  className="w-28 accent-blue-600"
                />
                <span className="text-sm font-medium text-gray-700 w-16">{sliderKw} kW</span>
              </div>
            )}
          </div>
        </div>

        {/* Overview tab */}
        {tab === 'overview' && (
          <div className="space-y-4">
            <AboutAnalysis introKey="capacity" />
            {(loadingError || feedersError) != null && (
              <ErrorBanner label="capacity data" error={loadingError ?? feedersError} />
            )}
            {(loadingError || feedersError) == null && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-3">
                {/* A6 shared freshness caption. Capacity is computed
                    per-request → "just now"; the AMI as-of segment is only
                    meaningful on the measured basis. */}
                <AsOfStamp
                  computedAt={null}
                  amiAsOf={loading?.load_source_requested === 'measured' ? loading.as_of : null}
                />
                {/* AMI measured-load basis note */}
                {loading && loading.load_source_requested === 'measured' && (
                  <div className="text-xs text-gray-500 border-b border-gray-100 pb-2">
                    {loading.as_of ? (
                      <span>
                        AMI measured load as of{' '}
                        <span className="font-medium text-gray-700">{loading.as_of}</span>
                        {loading.window_days != null && ` (${loading.window_days}-day window)`}.{' '}
                      </span>
                    ) : (
                      <span>No AMI measured load available; using model estimate.{' '}</span>
                    )}
                    <span>
                      {loading.measured_count} of {loading.total} transformers use measured
                      load; rest fall back to the {loading.avg_demand_kw_used} kW/meter estimate.
                    </span>
                  </div>
                )}
                <CapacitySummary
                  loading={loading}
                  feeders={feeders}
                  isLoading={loadingLoading || feedersLoading}
                  onSelectFeeder={handleSelectFeeder}
                />
              </div>
            )}

            {loading && loading.transformers.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <TransformerLoadingTable
                  transformers={loading.transformers}
                  avgDemandKw={avgDemandKw}
                  initialStatusFilter={deepLinkStatus}
                />
              </div>
            )}
          </div>
        )}

        {/* Feeder detail tab */}
        {tab === 'feeder' && selectedFeederId && (
          <div className="space-y-4">
            {/* Phase balance */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="mb-3"><AboutAnalysis introKey="phase-balance" /></div>
              {phaseError != null
                ? <ErrorBanner label="phase balance" error={phaseError} />
                : <PhaseBalanceChart data={phaseBalance} isLoading={phaseLoading} />}
            </div>

            {/* Transformer loading for this feeder */}
            {feederLoadingError != null && (
              <ErrorBanner label="transformer loading" error={feederLoadingError} />
            )}
            {feederLoading && feederLoading.transformers.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <TransformerLoadingTable
                  transformers={feederLoading.transformers}
                  avgDemandKw={avgDemandKw}
                  initialStatusFilter={deepLinkStatus}
                />
              </div>
            )}
            {feederLoadingLoading && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-sm text-gray-400">
                Loading transformer data...
              </div>
            )}

            {/* Voltage drop */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="mb-3"><AboutAnalysis introKey="voltage-drop" /></div>
              {vdropError != null
                ? <ErrorBanner label="voltage drop" error={vdropError} />
                : <VoltageDropSummary data={voltageDrop} isLoading={vdropLoading} />}
            </div>

            {/* What-if / new-load analysis */}
            {feederLoading && feederLoading.transformers.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="mb-3"><AboutAnalysis introKey="what-if" /></div>
                <WhatIfPanel
                  feederId={selectedFeederId}
                  avgDemandKw={avgDemandKw}
                  transformers={feederLoading.transformers.map(t => ({
                    id: t.id,
                    name: t.name,
                  }))}
                />
              </div>
            )}

            {/* Solve in OpenDSS — live power-flow through the real engine */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="mb-3"><AboutAnalysis introKey="opendss" /></div>
              <OpenDSSRunPanel feederId={selectedFeederId} avgDemandKw={avgDemandKw} />
            </div>
          </div>
        )}

        {/* AMI Measured tab */}
        {tab === 'ami' && (
          <AmiMeasuredTab selectedFeederId={selectedFeederId} avgDemandKw={avgDemandKw} />
        )}

        {/* Data-Quality tab (spec §7 item 7) */}
        {tab === 'quality' && (
          <div className="space-y-4">
            {/* Feeder selector (mirrors the fault tab so the tab is usable standalone) */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Select Feeder:</label>
                <select
                  className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={selectedFeederId ?? ''}
                  onChange={e => {
                    const val = e.target.value
                    setSelectedFeederId(val ? Number(val) : null)
                  }}
                >
                  <option value="">-- Choose a feeder --</option>
                  {sortedFeederOptions?.map(f => (
                    <option key={f.feeder_id} value={f.feeder_id}>
                      {f.feeder_name || `Feeder #${f.feeder_id}`}
                      {f.substation_name ? ` (${f.substation_name})` : ''}
                    </option>
                  ))}
                </select>
                {feedersLoading && (
                  <span className="text-xs text-gray-400">Loading feeders...</span>
                )}
              </div>
            </div>

            <AboutAnalysis introKey="data-quality" />
            <DataQualityTab feederId={selectedFeederId} />
          </div>
        )}

        {/* Fault analysis tab */}
        {tab === 'fault' && (
          <div className="space-y-4">
            {/* Feeder selector */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Select Feeder:</label>
                <select
                  className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={selectedFeederId ?? ''}
                  onChange={e => {
                    const val = e.target.value
                    setSelectedFeederId(val ? Number(val) : null)
                  }}
                >
                  <option value="">-- Choose a feeder --</option>
                  {sortedFeederOptions?.map(f => (
                    <option key={f.feeder_id} value={f.feeder_id}>
                      {f.feeder_name || `Feeder #${f.feeder_id}`}
                      {f.substation_name ? ` (${f.substation_name})` : ''}
                    </option>
                  ))}
                </select>
                {feedersLoading && (
                  <span className="text-xs text-gray-400">Loading feeders...</span>
                )}
              </div>
            </div>

            {selectedFeederId && (
              <>
                {/* Aggregated warnings — rendered ONCE per tab (spec §3.1);
                    Task s1-c1 removes the panels' own warning walls. The
                    component renders null while queries are in flight or when
                    every line is classified away. */}
                <WarningSummary warnings={faultTabWarnings} />

                {/* D-1 headline severity: device-duty rollup chips at the top
                    of the fault tab (replaces the removed 8 kA stat card). */}
                <DutyRollupChips feederId={selectedFeederId} />

                {/* Device-rating coverage — data-quality gate before duty/arc */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="mb-3"><AboutAnalysis introKey="device-ratings" /></div>
                  <DeviceRatingCoverageCard feederId={selectedFeederId} />
                </div>

                {/* Fault study */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="mb-3"><AboutAnalysis introKey="fault" /></div>
                  <FaultAnalysis feederId={selectedFeederId} />
                </div>

                {/* Latest-vs-live compare + persist snapshot */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="mb-3"><AboutAnalysis introKey="fault-compare" /></div>
                  <FaultStudyCompare feederId={selectedFeederId} />
                </div>

                {/* Device duty — anchor target for the rollup chips (D-1) */}
                <div
                  id={DEVICE_DUTY_CARD_ID}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 scroll-mt-4"
                >
                  <div className="mb-3"><AboutAnalysis introKey="duty" /></div>
                  <DeviceDuty feederId={selectedFeederId} />
                </div>

                {/* Protection coordination */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="mb-3"><AboutAnalysis introKey="coordination" /></div>
                  <ProtectionTree feederId={selectedFeederId} />
                </div>

                {/* Arc flash */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="mb-3"><AboutAnalysis introKey="arc-flash" /></div>
                  <ArcFlashSummary feederId={selectedFeederId} />
                </div>
              </>
            )}

            {!selectedFeederId && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <div className="text-sm text-gray-400">
                  Select a feeder above to run fault analysis
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/** Error state visually distinct from an empty state (spec §9 / AMI-tab
 *  pattern): a failed query must never read as "No data available". */
function ErrorBanner({ label, error }: { label: string; error: unknown }) {
  return (
    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
      {friendlyError(error, `load the ${label}`)} This is a request error, not
      an empty feeder.
    </div>
  )
}
